"use client";

import Modal from ".";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

interface ModalContextProps {
  show: (content: ReactNode, preventBackgroundClose?: boolean) => void;
  hide: () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [preventBackgroundClose, setPreventBackgroundClose] =
    useState<boolean>(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback(
    (content: ReactNode, preventClose?: boolean) => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setModalContent(content);
      setShowModal(true);
      setPreventBackgroundClose(!!preventClose);
    },
    [],
  );

  const hide = useCallback(() => {
    setShowModal(false);
    hideTimerRef.current = setTimeout(() => {
      setModalContent(null);
      hideTimerRef.current = null;
    }, 300);
  }, []);

  const contextValue = useMemo(() => ({ show, hide }), [show, hide]);

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {showModal && (
        <Modal
          showModal={showModal}
          setShowModal={setShowModal}
          preventBackgroundClose={preventBackgroundClose}
        >
          {modalContent}
        </Modal>
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  return useContext(ModalContext);
}
